/*
 * Typography preferences (#199): base text size, layout density and font
 * choice. This module is the ONE writer of the preference after load —
 * the pre-paint reader is client/public/theme-bootstrap.js, which applies
 * the same attributes before the first paint (see its header for why that
 * lives in its own classic script). The two share a vocabulary and storage
 * shape; config/__tests__/typography.test.js executes the bootstrap file
 * against this module's output to keep them from drifting apart.
 *
 * Storage is ONE JSON bag under localStorage['typography'] rather than a
 * key per control: the three values are one preference surface, set from
 * one panel, read by one bootstrap block. The pre-existing `theme` and
 * `readerMode` keys deliberately stay where they are — the theme key is
 * read by the load-bearing first lines of the bootstrap and by Layout's
 * toggle, readerMode by the detail pages' hook, and folding them in here
 * would risk both for no user-visible gain (decision recorded on #199).
 *
 * The CSS in index.css is the single source of what each attribute means
 * (the scale values, the font stacks, what compact tightens); this module
 * only flips attributes on <html>.
 */

export const TEXT_SIZES = ['small', 'medium', 'large', 'xlarge'];
export const DENSITIES = ['comfortable', 'compact'];
export const FONTS = ['serif', 'sans', 'dyslexic'];

export const DEFAULT_TYPOGRAPHY = {
  size: 'medium',
  density: 'comfortable',
  font: 'serif',
};

export const TEXT_SIZE_LABELS = {
  small: 'Small',
  medium: 'Medium',
  large: 'Large',
  xlarge: 'X-Large',
};

export const DENSITY_LABELS = {
  comfortable: 'Comfortable',
  compact: 'Compact',
};

export const FONT_LABELS = {
  serif: 'Serif (default)',
  sans: 'Sans-serif',
  dyslexic: 'OpenDyslexic',
};

const KEY = 'typography';

/** The stored preference, validated field by field — an unknown or
 *  corrupted value degrades to its default rather than poisoning the
 *  rest of the bag. */
export function getTypography() {
  let parsed = null;
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) parsed = JSON.parse(raw);
  } catch (err) {
    parsed = null;
  }
  if (!parsed || typeof parsed !== 'object') return { ...DEFAULT_TYPOGRAPHY };
  return {
    size: TEXT_SIZES.includes(parsed.size) ? parsed.size : DEFAULT_TYPOGRAPHY.size,
    density: DENSITIES.includes(parsed.density) ? parsed.density : DEFAULT_TYPOGRAPHY.density,
    font: FONTS.includes(parsed.font) ? parsed.font : DEFAULT_TYPOGRAPHY.font,
  };
}

/** Apply a (validated) preference to <html> — the same attributes the
 *  pre-paint bootstrap sets, so a change after load and a fresh load
 *  agree. A default value REMOVES its attribute: the no-preference
 *  document carries none, which keeps every pre-#199 snapshot and test
 *  expectation byte-identical. */
export function applyTypography(prefs) {
  const el = document.documentElement;
  if (prefs.size !== 'medium') el.setAttribute('data-textsize', prefs.size);
  else el.removeAttribute('data-textsize');
  if (prefs.density === 'compact') el.setAttribute('data-density', 'compact');
  else el.removeAttribute('data-density');
  if (prefs.font !== 'serif') el.setAttribute('data-font', prefs.font);
  else el.removeAttribute('data-font');
}

/** Merge a partial change into the stored preference, persist and apply.
 *  Returns the full new preference. When everything is back at the
 *  defaults the key is removed entirely (like readerMode's off state). */
export function setTypography(partial) {
  const next = { ...getTypography(), ...partial };
  // Re-validate the merge so a bad caller value cannot be persisted.
  const clean = {
    size: TEXT_SIZES.includes(next.size) ? next.size : DEFAULT_TYPOGRAPHY.size,
    density: DENSITIES.includes(next.density) ? next.density : DEFAULT_TYPOGRAPHY.density,
    font: FONTS.includes(next.font) ? next.font : DEFAULT_TYPOGRAPHY.font,
  };
  const isDefault =
    clean.size === DEFAULT_TYPOGRAPHY.size &&
    clean.density === DEFAULT_TYPOGRAPHY.density &&
    clean.font === DEFAULT_TYPOGRAPHY.font;
  try {
    if (isDefault) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, JSON.stringify(clean));
  } catch (err) {
    /* private mode etc. — the in-page change still applies */
  }
  applyTypography(clean);
  return clean;
}

/** Back to the defaults (clears storage and attributes). */
export function resetTypography() {
  return setTypography({ ...DEFAULT_TYPOGRAPHY });
}
